import CustomToolbar from '@/contexts/shared/components/CustomToolbar'
import TableLanguagesCell from '@/contexts/shared/components/table/TableLanguagesCell'
import { Box, Typography } from '@mui/material'
import { DataGrid, GridActionsCellItem, GridCellParams, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { assignHighlight, getSeminars, showSeminar, toggleFormSeminar } from '../../../../../redux/actions/seminars'
import { IRootState } from '../../../../../redux/types'
import HighlightIcon from '../../../../shared/components/icons/HighlightIcon'
import VisibilityIcon from '../../../../shared/components/icons/VisibilityIcon'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'
import { Seminar } from '../../models/Seminars'
import SeminarDeleteModal from './SeminarDeleteModal'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ViewIcon from '@mui/icons-material/Visibility'

function SeminarsTable() {
  const { t } = useTranslation(['global'])
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { seminars, reload } = useSelector((state: IRootState) => state.seminar)
  const isMobile = useScreenWidth(700)

  const [openDeleteSeminar, setOpenDeleteSeminar] = useState(false)
  const [seminarSelected, setSeminarSelected] = useState<number | null>(null)

  useEffect(() => {
    if (reload) dispatch(getSeminars())
  }, [reload])

  const handleHighlight = (id: number, isHighlight: boolean) => {
    dispatch(assignHighlight({ id, isHighlight }))
  }

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: 'title',
        headerName: t('title'),
        flex: 5,
        renderCell: (params: GridRenderCellParams) => (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography>{params.row.title}</Typography>
            <HighlightIcon id={params.row.id} isHighlight={params.row.highlight} handleClick={handleHighlight} />
            <VisibilityIcon id={params.row.visibility} />
          </Box>
        )
      },
      {
        field: 'created_at',
        headerName: t('date'),
        flex: 1,
        renderCell: (params: GridCellParams) => {
          if (params.row.created_at != null) {
            return dayjs(params.row.created_at).format('DD MMM YYYY')
          }
          return '--'
        }
      },
      {
        field: 'actions',
        type: 'actions',
        sortable: false,
        filterable: false,
        maxWidth: 70,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            key={params.id}
            icon={<ViewIcon />}
            label={t('view')}
            onClick={() => navigate(`${params.row.id}`)}
            showInMenu
          />,
          <GridActionsCellItem
            key={params.id}
            icon={<EditIcon />}
            label={t('edit')}
            onClick={() => {
              dispatch(showSeminar(params.row.id))
              dispatch(toggleFormSeminar())
            }}
            showInMenu
          />,
          <GridActionsCellItem
            key={params.id}
            icon={<DeleteIcon />}
            label={t('delete')}
            onClick={() => {
              setSeminarSelected(params.row.id)
              setOpenDeleteSeminar(true)
            }}
            showInMenu
          />
        ]
      }
    ]

    if (!isMobile) {
      cols.splice(
        1,
        0,
        {
          field: 'brandName',
          headerName: t('brand'),
          flex: 1
        },
        {
          field: 'languageCode',
          headerName: t('language'),
          flex: 0.5,
          renderCell: (params: GridCellParams) => <TableLanguagesCell params={params} />
        },
        {
          field: 'roleName',
          headerName: t('rol'),
          flex: 1
        }
      )
    }

    return cols
  }, [t, isMobile])

  return (
    <>
      <DataGrid
        rows={seminars}
        columns={columns}
        getRowId={(row: Seminar) => row.id}
        pagination
        pageSizeOptions={[10, 20]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        autoHeight
        density="comfortable"
        slots={{
          toolbar: CustomToolbar
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 }
          }
        }}
      />
      <SeminarDeleteModal open={openDeleteSeminar} setOpen={setOpenDeleteSeminar} seminar_id={seminarSelected} />
    </>
  )
}

export default SeminarsTable
